import { createBrowserRouter, Navigate } from "react-router";
import {
  AUTH_ROUTES,
  DuviSharedAdminLoginPasswordEntry,
  DuviSharedAdminLoginMFAChallenge,
  DuviSharedAdminSetNewPasswordForm,
  DuviSharedAdminForgotPasswordEmail,
  DuviSharedAdminForgotPasswordCheckEmail,
  DuviSharedAdminResetPasswordForm,
  DuviSharedAdminResetPasswordSuccess,
  DuviSharedAdminSetupMFAIntroduction,
  DuviSharedAdminSetupMFAQRCode,
  DuviSharedAdminSetupMFAVerifyAndBackup,
  DuviSharedAdminSetupComplete,
  DuviSharedAdminProtectedRoute,
} from "@duvi-agency/auth";
import { DuviCoreAdminDashboard } from "./pages/duvi-core-admin-dashboard";
import { DuviCoreAdminKnowledgeBaseList } from "./pages/duvi-core-admin-knowledge-base-list";
import { DuviCoreAdminKnowledgeBaseEditor } from "./pages/duvi-core-admin-knowledge-base-editor";
import { DuviCoreAdminSupportTicketsList } from "./pages/duvi-core-admin-support-tickets-list";
import { DuviCoreAdminSupportTicketDetail } from "./pages/duvi-core-admin-support-ticket-detail";
import { DuviCoreAdminUserManagement } from "./pages/duvi-core-admin-user-management";
import { DuviCoreAdminUserDetail } from "./pages/duvi-core-admin-user-detail";
import { DuviCoreAdminAuditLog } from "./pages/duvi-core-admin-audit-log";
import { DuviCoreAdminNichesList } from "./pages/duvi-core-admin-niches-list";
import { DuviCoreAdminSettingsPage } from "./pages/duvi-core-admin-settings-page";
import { DuviCoreAdminAccessDenied } from "./pages/duvi-core-admin-access-denied";

/**
 * Wraps children in ProtectedRoute gated on the platform_admin role.
 * Authenticated users without the role are redirected to /access-denied.
 */
function Protected({ children }: { children: React.ReactNode }) {
  return (
    <DuviSharedAdminProtectedRoute requiredRole="platform_admin">
      {children}
    </DuviSharedAdminProtectedRoute>
  );
}

/**
 * Legacy `/auth/*` client-route redirects (story 35, epic 031).
 *
 * The platform-admin app (story 28) originally registered its auth screens
 * under `/auth/*`. The consolidated CloudFront edge reverse-proxies `/auth/*`
 * to the auth API, so any full-page navigation to those paths (refresh,
 * bookmark, direct link, OAuth redirect-back) hit the API (JSON/404) instead
 * of the SPA. The canonical scheme now lives in `AUTH_ROUTES` (from
 * `@duvi-agency/auth@0.9.0`), none of which start with `/auth/`.
 *
 * These entries keep existing bookmarks / deep links working by client-side
 * redirecting each old path onto its `AUTH_ROUTES` equivalent. The trailing
 * `/auth/*` splat catches anything unmapped and lands on sign-in. (Note: a
 * hard-refresh of a bare `/auth/*` URL still hits the edge → API; these
 * redirects cover in-app navigation and any `/auth/*` link that reaches the
 * SPA. The routes themselves have moved off `/auth/*` entirely, which is the
 * actual fix.)
 */
const legacyAuthRedirects = [
  { path: "/auth", to: AUTH_ROUTES.login },
  { path: "/auth/password", to: AUTH_ROUTES.login },
  { path: "/auth/mfa", to: AUTH_ROUTES.loginMfa },
  { path: "/auth/new-password", to: AUTH_ROUTES.setPassword },
  { path: "/auth/forgot-password", to: AUTH_ROUTES.forgotPassword },
  {
    path: "/auth/forgot-password/check-email",
    to: AUTH_ROUTES.forgotPasswordCheckEmail,
  },
  { path: "/auth/reset-password", to: AUTH_ROUTES.resetPassword },
  {
    path: "/auth/reset-password/success",
    to: AUTH_ROUTES.resetPasswordSuccess,
  },
  // Catch-all for any other legacy `/auth/*` path.
  { path: "/auth/*", to: AUTH_ROUTES.login },
].map(({ path, to }) => ({
  path,
  element: <Navigate to={to} replace />,
}));

/**
 * The full route table. Exported (alongside the constructed `router`) so tests
 * can mount it under a `createMemoryRouter` at arbitrary initial entries.
 */
export const routes = [
  // Root renders the sign-in entry directly so that a bare `/` (and the
  // session-expired hard-reload target — see App.tsx) always lands on the SPA.
  {
    path: "/",
    element: <DuviSharedAdminLoginPasswordEntry dashboardPath="/dashboard" />,
  },

  // --- Auth surface — canonical AUTH_ROUTES scheme (story 34/35) ---
  {
    path: AUTH_ROUTES.login,
    element: <DuviSharedAdminLoginPasswordEntry dashboardPath="/dashboard" />,
  },
  {
    path: AUTH_ROUTES.loginMfa,
    element: <DuviSharedAdminLoginMFAChallenge dashboardPath="/dashboard" />,
  },
  {
    path: AUTH_ROUTES.setPassword,
    element: <DuviSharedAdminSetNewPasswordForm />,
  },
  {
    path: AUTH_ROUTES.forgotPassword,
    element: <DuviSharedAdminForgotPasswordEmail />,
  },
  {
    path: AUTH_ROUTES.forgotPasswordCheckEmail,
    element: <DuviSharedAdminForgotPasswordCheckEmail />,
  },
  {
    path: AUTH_ROUTES.resetPassword,
    element: <DuviSharedAdminResetPasswordForm />,
  },
  {
    path: AUTH_ROUTES.resetPasswordSuccess,
    element: <DuviSharedAdminResetPasswordSuccess />,
  },

  // --- MFA enrolment flow ---
  // Entry point is the canonical AUTH_ROUTES.mfaSetup (`/login/mfa-setup`),
  // where the ProtectedRoute / login hooks send first-time enrolments. The
  // subsequent step screens live under `/setup/*` (the package's own internal
  // navigation targets — NOT under `/auth/*`, so unaffected by the edge proxy).
  {
    path: AUTH_ROUTES.mfaSetup,
    element: <DuviSharedAdminSetupMFAIntroduction />,
  },
  { path: "/setup/mfa/scan", element: <DuviSharedAdminSetupMFAQRCode /> },
  {
    path: "/setup/mfa/verify",
    element: <DuviSharedAdminSetupMFAVerifyAndBackup />,
  },
  { path: "/setup/complete", element: <DuviSharedAdminSetupComplete /> },

  // --- Legacy `/auth/*` redirects (bookmarks / deep links) ---
  ...legacyAuthRedirects,

  // --- Protected app routes ---
  {
    path: "/dashboard",
    element: (
      <Protected>
        <DuviCoreAdminDashboard />
      </Protected>
    ),
  },
  {
    path: "/knowledge-base",
    children: [
      {
        index: true,
        element: (
          <Protected>
            <DuviCoreAdminKnowledgeBaseList />
          </Protected>
        ),
      },
      {
        path: "new",
        element: (
          <Protected>
            <DuviCoreAdminKnowledgeBaseEditor />
          </Protected>
        ),
      },
      {
        path: ":id",
        element: (
          <Protected>
            <DuviCoreAdminKnowledgeBaseEditor />
          </Protected>
        ),
      },
    ],
  },
  {
    path: "/support/tickets",
    children: [
      {
        index: true,
        element: (
          <Protected>
            <DuviCoreAdminSupportTicketsList />
          </Protected>
        ),
      },
      {
        path: ":id",
        element: (
          <Protected>
            <DuviCoreAdminSupportTicketDetail />
          </Protected>
        ),
      },
    ],
  },
  {
    path: "/user-management",
    children: [
      {
        index: true,
        element: (
          <Protected>
            <DuviCoreAdminUserManagement />
          </Protected>
        ),
      },
      {
        path: ":userId",
        element: (
          <Protected>
            <DuviCoreAdminUserDetail />
          </Protected>
        ),
      },
    ],
  },
  {
    path: "/platform/niches",
    element: (
      <Protected>
        <DuviCoreAdminNichesList />
      </Protected>
    ),
  },
  {
    path: "/audit-log",
    element: (
      <Protected>
        <DuviCoreAdminAuditLog />
      </Protected>
    ),
  },
  {
    path: "/settings",
    element: (
      <Protected>
        <DuviCoreAdminSettingsPage />
      </Protected>
    ),
  },
  {
    path: "/access-denied",
    element: <DuviCoreAdminAccessDenied />,
  },
];

export const router = createBrowserRouter(routes);
